import { c as _c } from "react/compiler-runtime";
import { Form, FormikProvider, useFormik } from 'formik';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import APIMethodKeys from '../../../client/APIMethodKeys';
import { EventListFiltersProps } from '../../../typings/EventList.interface';
import { RequestListFiltersProps } from '../../../typings/RequestList.interface';
import { GlobalContext } from '../../contexts/GlobalContext';
import { UserContext } from '../../contexts/UserContext';
import { useDatePreferences } from '../../hooks/useDatePreferences';
import { DashboardContext } from '../../scenes/DashboardScene/DashboardContext';
import Button from '../base/Button';
import FilterChip from '../base/FilterChip';
import Dropdown from '../Dropdown';
import { Div } from '../helpers/StyledUtils';
import { FilterComponent, FilterFormValues } from './FilterComponents';
import { MoreFiltersMenu } from './MoreFiltersMenu';
import { OutpostAttemptFilters } from '../../scenes/OutpostScene/Attempts/utils';
import { OutpostEventFilters } from '../../scenes/OutpostScene/Events/EventsView';
export const StyledFilterButtonsWrapper = styled(Div)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${({
  theme
}) => theme.spacing(2)};
  overflow-x: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  > * {
    flex-shrink: 0;
  }
`;
interface FiltersProps {
  route: string;
  filters: EventListFiltersProps | RequestListFiltersProps | OutpostEventFilters | OutpostAttemptFilters;
  unstructured_component?: FilterComponent;
  components: FilterComponent[];
  onFilterChanged: (filters: object) => void;
}
export const FiltersContext = createContext<{
  filters: EventListFiltersProps | RequestListFiltersProps | OutpostEventFilters | OutpostAttemptFilters;
  components: FilterComponent[];
  active_filters: FilterComponent[];
  filter_buttons: FilterComponent[];
  route: string;
  unstructured_component?: FilterComponent;
  values: FilterFormValues;
  formik: ReturnType<typeof useFormik<FilterFormValues>>;
  handleToggleActiveFilter: (key: string) => void;
  getDeleteButton: (filter: FilterComponent) => JSX.Element;
  active_filter_without_unstructured: FilterComponent[];
  removeFilter: (filter: FilterComponent) => void;
}>({
  filters: {},
  components: [],
  active_filters: [],
  filter_buttons: [],
  route: '',
  values: {} as FilterFormValues,
  formik: {} as ReturnType<typeof useFormik<FilterFormValues>>,
  handleToggleActiveFilter: () => {
    return;
  },
  getDeleteButton: () => {
    return <></>;
  },
  active_filter_without_unstructured: [],
  removeFilter: () => {
    return;
  }
});
const FiltersProvider: React.FC<FiltersProps & {
  children: React.ReactNode;
}> = ({
  filters,
  components,
  route,
  onFilterChanged,
  unstructured_component,
  children
}) => {
  if (unstructured_component) {
    components = [...components, unstructured_component];
  }
  const {
    team
  } = useContext(DashboardContext);
  const [active_filters, setActiveFilters] = useState<FilterComponent[]>([]);
  const [save_error, setSaveError] = useState<string | undefined>(undefined);
  // Serialized URL-derived form values from the last run of the sync effect below.
  // Re-syncing only when this content changes (rather than comparing against live formik
  // values) keeps unsubmitted edits — e.g. JSON mid-typing in an open filter dropdown —
  // from being clobbered when a background list refresh re-renders the parent and passes
  // a same-content, new-identity `filters` object.
  const last_synced_values_ref = useRef<string | undefined>(undefined);
  useEffect(() => {
    const new_active_filters: FilterComponent[] = [...active_filters];
    const active_filters_by_key = active_filters.reduce((object, f) => ({
      ...object,
      [f.filter_key]: f
    }), {});
    let active_filters_changed = false;
    const new_values = {};
    components.forEach(f_0 => {
      const formattedValue = f_0.formatForForm(filters[f_0.filter_key], filters);
      new_values[f_0.form_name] = formattedValue;
      const active = f_0.isActive(formattedValue);
      if (active && !active_filters_by_key[f_0.filter_key]) {
        new_active_filters.push(f_0);
        active_filters_by_key[f_0.filter_key] = f_0;
        active_filters_changed = true;
        return;
      } else if (!active) {
        delete active_filters_by_key[f_0.filter_key];
        const index = new_active_filters.findIndex(a => a.filter_key === f_0.filter_key);
        if (index !== -1) {
          new_active_filters.splice(index, 1);
          active_filters_changed = true;
        }
      }
    });

    // Only write state when the URL-derived content actually changed. `filters` gets a
    // new identity on every parent render; unconditionally calling setValues here
    // dispatches SET_VALUES and kicks off a full validation pass (SET_ISVALIDATING
    // true/false + SET_ERRORS) each time, which can cascade into "Maximum update depth
    // exceeded" — and overwriting formik with URL state while the user has unsubmitted
    // edits wipes what they are typing.
    if (active_filters_changed) {
      setActiveFilters(new_active_filters);
    }
    const serialized_values = JSON.stringify(new_values);
    if (last_synced_values_ref.current !== serialized_values) {
      const is_first_sync = last_synced_values_ref.current === undefined;
      last_synced_values_ref.current = serialized_values;
      // On mount formik was initialized from this same `filters`, so just record the
      // baseline; dispatching setValues would only trigger a redundant validation pass.
      if (!is_first_sync) {
        setValues(new_values);
      }
    }
  }, [filters]);
  useEffect(() => {
    const new_active_filters_0 = active_filters.filter(f_1 => !!components.find(filter => f_1.filter_key === filter.filter_key));
    if (new_active_filters_0.length !== active_filters.length) {
      const new_values_0 = {
        ...values
      };
      const removed_keys = Object.keys(new_values_0).filter(key => !new_active_filters_0.find(f_2 => f_2.filter_key === key));
      removed_keys.forEach(key_0 => delete new_values_0[key_0]);
      setActiveFilters(new_active_filters_0);
      setValues(new_values_0);
    }
  }, [components]);
  const formik = useFormik<FilterFormValues>({
    initialValues: components.reduce((obj, f_3) => {
      obj[f_3.form_name] = f_3.formatForForm(filters[f_3.filter_key], filters);
      return obj;
    }, {}) as FilterFormValues,
    validate: values_0 => {
      return active_filters.filter(f_4 => !!f_4.validate).reduce((errors, filter_0) => {
        const filter_errors = filter_0.validate?.(values_0[filter_0.form_name]);
        if (filter_errors && Object.keys(filter_errors).length > 0) {
          errors[filter_0.form_name] = filter_errors;
        }
        return errors;
      }, {});
    },
    onSubmit: values_1 => {
      let should_update = false;
      components.forEach(f_5 => {
        const formated_filter = f_5.formatForForm(filters[f_5.filter_key], filters);
        if (!f_5.isActive(values_1[f_5.form_name]) && !f_5.isActive(formated_filter)) {
          return;
        }
        if (JSON.stringify(values_1[f_5.form_name]) !== JSON.stringify(formated_filter)) {
          should_update = true;
        }
      });
      if (!should_update) {
        return;
      }
      const new_filters = components.reduce((query, {
        filter_key,
        form_name,
        formatForQuery
      }) => {
        const formatted_value = formatForQuery(values_1[form_name]);

        // Handle nested filter keys like 'request.body', 'request.headers'
        if (filter_key.includes('.')) {
          const [parent_key] = filter_key.split('.');
          if (formatted_value !== undefined) {
            if (!query[parent_key]) {
              query[parent_key] = {};
            }
            query[parent_key] = {
              ...(query[parent_key] as object),
              ...(formatted_value as object)
            };
          }
        } else {
          query[filter_key] = formatted_value;
        }
        return query;
      }, {});
      onFilterChanged(new_filters);
      if (save_error) {
        setSaveError(undefined);
      }
    }
  });
  const {
    handleSubmit,
    setValues,
    values
  } = formik;
  const filters_by_key: {
    [key: string]: FilterComponent;
  } = (() => {
    return components.reduce((object_0, f_6) => ({
      ...object_0,
      [f_6.filter_key]: f_6
    }), {});
  })();
  const handleToggleActiveFilter = (key_1: string) => {
    const filter_1 = active_filters.find(a_0 => a_0.filter_key === key_1);
    if (filter_1) {
      const new_active_filters_1 = [...active_filters];
      new_active_filters_1.splice(active_filters.findIndex(f_7 => f_7.filter_key === key_1), 1);
      setActiveFilters(new_active_filters_1);
      const new_filters_by_key = {
        ...filters_by_key
      };
      new_active_filters_1.forEach(f_8 => delete new_filters_by_key[f_8.filter_key]);
      handleSubmit();
      setValues(prev => ({
        ...prev,
        [filter_1.form_name]: filter_1.formatForForm(null, {})
      }));
    } else {
      setActiveFilters(prev_0 => [...prev_0, filters_by_key[key_1]]);
    }
  };
  const removeFilter = (filter_2: FilterComponent) => {
    handleToggleActiveFilter(filter_2.filter_key);
  };
  const getDeleteButton = (filter_3: FilterComponent) => {
    return <Button ghost large icon="close" onClick={() => removeFilter(filter_3)} m={{
      l: 2
    }} />;
  };
  const filter_buttons = components.filter(f_9 => !f_9.hidden || active_filters.some(a_1 => a_1.filter_key === f_9.filter_key));
  const active_filter_without_unstructured = active_filters.filter(a_2 => unstructured_component ? a_2.filter_key !== unstructured_component?.filter_key : true);
  const contextValue = {
    filters,
    components,
    active_filters,
    filter_buttons,
    route,
    unstructured_component,
    values,
    formik,
    handleToggleActiveFilter,
    getDeleteButton,
    active_filter_without_unstructured,
    removeFilter
  };
  return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
};
const FilterButtons: React.FC = () => {
  const $ = _c(31);
  const {
    filter_buttons,
    active_filters,
    filters,
    formik,
    removeFilter
  } = useContext(FiltersContext);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    user
  } = useContext(UserContext);
  const {
    team
  } = useContext(DashboardContext);
  const {
    timezone
  } = useDatePreferences();
  const webhook_id = formik.values.webhook_id || [];
  const destination_id = formik.values.destination_id || [];
  const source_id = formik.values.source_id || [];
  const tenant_id = formik.values.tenant_id || [];
  const unique_webhook_ids = Array.from(new Set(webhook_id)).sort();
  const unique_destination_ids = Array.from(new Set(destination_id)).sort();
  const unique_source_ids = Array.from(new Set(source_id)).sort();
  const unique_tenant_ids = Array.from(new Set(tenant_id)).sort();
  const {
    unique_webhook_ids: unique_webhook_ids_0,
    unique_destination_ids: unique_destination_ids_0,
    unique_source_ids: unique_source_ids_0,
    unique_tenant_ids: unique_tenant_ids_0
  } = {
    unique_webhook_ids,
    unique_destination_ids,
    unique_source_ids,
    unique_tenant_ids
  };
  const {
    data: webhooks
  } = useSWR(unique_webhook_ids_0.length > 0 ? APIMethodKeys.webhooks.list({
    id: unique_webhook_ids_0
  }) : null, () => HookdeckAPI.webhooks.list(unique_webhook_ids_0.length > 0 ? {
    id: unique_webhook_ids_0,
    limit: unique_webhook_ids_0.length
  } : {
    limit: 1
  }));
  const {
    data: destinations
  } = useSWR(unique_destination_ids_0.length > 0 ? APIMethodKeys.destinations.list({
    id: unique_destination_ids_0
  }) : null, () => HookdeckAPI.destinations.list(unique_destination_ids_0.length > 0 ? {
    id: unique_destination_ids_0,
    limit: unique_destination_ids_0.length
  } : {
    limit: 1
  }));
  const {
    data: sources
  } = useSWR(unique_source_ids_0.length > 0 ? APIMethodKeys.sources.list({
    id: unique_source_ids_0
  }) : null, () => HookdeckAPI.sources.list(unique_source_ids_0.length > 0 ? {
    id: unique_source_ids_0,
    limit: unique_source_ids_0.length
  } : {
    limit: 1
  }));
  let t0;
  if ($[0] !== HookdeckAPI.outpost.tenants || $[1] !== unique_tenant_ids_0) {
    t0 = () => HookdeckAPI.outpost.tenants.list(unique_tenant_ids_0.length > 0 ? {
      id: unique_tenant_ids_0,
      limit: unique_tenant_ids_0.length
    } : {
      limit: 1
    });
    $[0] = HookdeckAPI.outpost.tenants;
    $[1] = unique_tenant_ids_0;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    data: tenants
  } = useSWR(unique_tenant_ids_0.length > 0 ? APIMethodKeys.outpost.tenants.list({
    id: unique_tenant_ids_0
  }) : null, t0);
  let t1;
  if ($[3] !== team.id) {
    t1 = APIMethodKeys.teams.listMembers(team.id);
    $[3] = team.id;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== HookdeckAPI.teams) {
    t2 = () => HookdeckAPI.teams.listMembers();
    $[5] = HookdeckAPI.teams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    data: team_members
  } = useSWR(t1, t2);
  let T0;
  let secondary_filters;
  let t3;
  if ($[7] !== active_filters || $[8] !== destinations || $[9] !== filter_buttons || $[10] !== filters || $[11] !== formik || $[12] !== removeFilter || $[13] !== sources || $[14] !== team_members || $[15] !== tenants || $[16] !== timezone || $[17] !== user || $[18] !== webhooks) {
    const webhooks_by_id = webhooks ? webhooks.models.reduce(_temp, {}) : {};
    const destinations_by_id = destinations ? destinations.models.reduce(_temp2, {}) : {};
    const sources_by_id = sources ? sources.models.reduce(_temp3, {}) : {};
    const tenants_by_id = tenants ? tenants.models.reduce(_temp4, {}) : {};
    const team_members_by_id = team_members ? team_members.reduce(_temp5, {}) : {};
    const data_by_id = {
      webhook_id: webhooks_by_id,
      destination_id: destinations_by_id,
      source_id: sources_by_id,
      team_members: team_members_by_id,
      tenant_id: tenants_by_id
    };
    let t4;
    if ($[22] !== active_filters) {
      t4 = (filters_0, f) => {
        if (f.priority === "primary" || active_filters.some(a => a.filter_key === f.filter_key)) {
          filters_0.primary_filters.push(f);
        } else {
          if (f.priority === "secondary") {
            filters_0.secondary_filters.push(f);
          }
        }
        return filters_0;
      };
      $[22] = active_filters;
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    const {
      primary_filters,
      secondary_filters: t5
    } = filter_buttons.reduce(t4, {
      primary_filters: [],
      secondary_filters: []
    } as {
      primary_filters: FilterComponent[];
      secondary_filters: FilterComponent[];
    });
    secondary_filters = t5;
    T0 = StyledFilterButtonsWrapper;
    t3 = primary_filters.map(f_0 => {
      const filter = active_filters.find(a_0 => a_0.filter_key === f_0.filter_key);
      const is_active = !!filter;
      const values = formik.values[f_0.form_name];
      return <Dropdown key={f_0.filter_key} portal={true} renderToggle={(opened, handleToggle) => <FilterChip key={f_0.filter_key} label={f_0.label} label_icon={f_0.icon} value={filter?.valuesToString(values, data_by_id, user || undefined, timezone)} m={{
        y: 1
      }} dashed={!is_active} icon={is_active ? "close" : "add"} active={opened} hide_chevron={!f_0.Component} onClick={() => {
        if (!f_0.Component) {
          return null;
        }
        handleToggle(true);
      }} onIconClick={is_active ? () => removeFilter(f_0) : undefined} />} onToggle={opened_0 => {
        if (!opened_0) {
          formik.handleSubmit();
        }
      }} parent_width={{
        min: f_0.width || 300,
        max: f_0.width || 300
      }}>{toggle => f_0.Component && <f_0.Component key={f_0.filter_key} label={f_0.label} name={f_0.form_name} filters={filters} {...f_0.component_props as any} onSubmit={() => {
          formik.handleSubmit();
          toggle(false);
        }} onReset={formik.resetForm} />}</Dropdown>;
    });
    $[7] = active_filters;
    $[8] = destinations;
    $[9] = filter_buttons;
    $[10] = filters;
    $[11] = formik;
    $[12] = removeFilter;
    $[13] = sources;
    $[14] = team_members;
    $[15] = tenants;
    $[16] = timezone;
    $[17] = user;
    $[18] = webhooks;
    $[19] = T0;
    $[20] = secondary_filters;
    $[21] = t3;
  } else {
    T0 = $[19];
    secondary_filters = $[20];
    t3 = $[21];
  }
  let t4;
  if ($[24] !== formik || $[25] !== secondary_filters) {
    t4 = secondary_filters.length > 0 && <MoreFiltersMenu filters={secondary_filters} onToggle={opened_1 => {
      if (!opened_1) {
        formik.handleSubmit();
      }
    }} />;
    $[24] = formik;
    $[25] = secondary_filters;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  let t5;
  if ($[27] !== T0 || $[28] !== t3 || $[29] !== t4) {
    t5 = <T0>{t3}{t4}</T0>;
    $[27] = T0;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  return t5;
};
const FilterForm: React.FC = () => {
  const $ = _c(3);
  const {
    formik
  } = useContext(FiltersContext);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Form id="filters-form"><FilterButtons /></Form>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== formik) {
    t1 = <FormikProvider value={formik}>{t0}</FormikProvider>;
    $[1] = formik;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

// Create the main Filters component and add subcomponents
const Filters = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Div w={100}><FilterForm /></Div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <FiltersProvider {...props}>{t0}</FiltersProvider>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
Filters.Form = FilterForm;
Filters.Provider = FiltersProvider;
export default Filters;
function _temp(object, webhook) {
  return {
    ...object,
    [webhook.id]: webhook
  };
}
function _temp2(object_0, destination) {
  return {
    ...object_0,
    [destination.id]: destination
  };
}
function _temp3(object_1, source) {
  return {
    ...object_1,
    [source.id]: source
  };
}
function _temp4(object_2, tenant) {
  return {
    ...object_2,
    [tenant.id]: tenant
  };
}
function _temp5(object_3, team_member) {
  return {
    ...object_3,
    [team_member.user_id]: team_member
  };
}